import { Client, CostCentre, ProjectDetails } from '@/types';
import { getCostCentresForProject } from '@/lib/mock-data';

/**
 * Cost centres selectable for a project, resolved the same way in both data modes:
 * nested cost centres first (live data nests them by projectCode → billingCode in
 * SchedulerContext.fetchData), then the project code match, then — demo only — the
 * static project → cost centre mapping.
 */
export function getProjectCostCentres(
  projectId: string | number | null | undefined,
  projects: ProjectDetails[] | undefined,
  costCentres: CostCentre[] | undefined,
  mode: 'demo' | 'live',
): CostCentre[] {
  const allCostCentres = costCentres || [];
  if (!projectId) return [];

  const project = projects?.find(p => String(p.id) === String(projectId));

  const nested = project?.costCentres ?? [];
  if (nested.length > 0) return nested;

  if (project?.billingCode) {
    const byCode = allCostCentres.filter(cc =>
      cc.projectCode != null && cc.projectCode === project.billingCode
    );
    if (byCode.length > 0) return byCode;
  }

  if (mode === 'demo') {
    const mappedIds = getCostCentresForProject(String(projectId));
    if (mappedIds.length > 0) {
      return allCostCentres.filter(cc => mappedIds.includes(String(cc.id)));
    }
  }

  return [];
}

/** The cost centre with `costCentreId`, from the flat list or any project's nested list. */
function findCostCentreById(
  costCentreId: string | number,
  projects: ProjectDetails[] | undefined,
  costCentres: CostCentre[] | undefined,
): CostCentre | undefined {
  const matches = (cc: CostCentre) => String(cc.id) === String(costCentreId);
  return (costCentres || []).find(matches)
    ?? (projects || []).flatMap(p => p.costCentres ?? []).find(matches);
}

/** Whether a booking's cost centre can be kept when it moves to `projectId`. */
export function isCostCentreValidForProject(
  costCentreId: string | number | null | undefined,
  projectId: string | number | null | undefined,
  projects: ProjectDetails[] | undefined,
  costCentres: CostCentre[] | undefined,
  mode: 'demo' | 'live',
): boolean {
  // Nothing to invalidate when the booking carries no cost centre.
  if (costCentreId == null || String(costCentreId) === '') return true;

  const available = getProjectCostCentres(projectId, projects, costCentres, mode);
  // A project with no cost centres at all offers nothing to pick, so the booking keeps
  // the one it has rather than being sent to an empty picker.
  if (available.length === 0) return true;

  // Projects sharing a cost centre each nest their own record for it, with distinct ids
  // but the same code, so the code identifies the cost centre across projects.
  const code = findCostCentreById(costCentreId, projects, costCentres)?.code;

  return available.some(cc =>
    String(cc.id) === String(costCentreId) || (!!code && cc.code === code)
  );
}

const splitCsv = (value?: string | null): string[] =>
  value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];

const matchesValue = (value: string | number | null | undefined, candidate: string): boolean =>
  value != null && String(value).trim().toLowerCase() === candidate.trim().toLowerCase();

/**
 * Whether a booking's customer can be kept when it moves to `projectId`.
 * Customer data can arrive as an id, code, or display name depending on the
 * source system and data mode, so compare against every project client signal.
 */
export function isCustomerValidForProject(
  customerId: string | number | null | undefined,
  projectId: string | number | null | undefined,
  projects: ProjectDetails[] | undefined,
  clients: Client[] | undefined,
  _mode: 'demo' | 'live',
): boolean {
  if (customerId == null || String(customerId).trim() === '') return true;

  const project = projects?.find(p => String(p.id) === String(projectId));
  if (!project) return false;

  const rawCustomer = String(customerId).trim();
  const client = (clients ?? []).find(c =>
    matchesValue(c.id, rawCustomer)
    || matchesValue(c.code, rawCustomer)
    || matchesValue(c.name, rawCustomer)
  );

  const projectIds = [
    project.clientId,
    ...splitCsv(project.clientIds),
    ...splitCsv(project.clientCodes),
    ...splitCsv(project.clientNames),
    project.clientName,
  ];

  if (client) {
    return projectIds.some(value =>
      matchesValue(value, String(client.id))
      || matchesValue(value, client.code ?? '')
      || matchesValue(value, client.name)
    );
  }

  return projectIds.some(value => matchesValue(value, rawCustomer));
}
